import {
    Typography, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Chip,
    CircularProgress, Snackbar, Alert, IconButton, Tooltip, Autocomplete
} from '@mui/material'
import { Add, Edit, Block, CheckCircleOutline } from '@mui/icons-material'
import { FC, useCallback, useEffect, useState } from 'react'
import EmptyView from '../common/EmptyView'
import Show from '../common/show'
import { Api } from '../../helpers'
import _ from 'lodash'

interface Staff {
    _id: string
    name: string
    phone: string
    active: boolean
}

interface UserOption {
    _id: string
    Username: string
    Phone: string
}

interface FormState {
    name: string
}

interface StateProps {
    staff: Staff[]
    loading: boolean
    showModal: boolean
    editingId: string | null
    togglingId: string | null
    saving: boolean
    form: FormState
    error: string | null
    userOptions: UserOption[]
    usersLoading: boolean
    selectedUser: UserOption | null
}

const defaultForm: FormState = { name: '' }

const headers = ['Name', 'Phone', 'Status', 'Actions']

// Near-verbatim clone of Installers/index.tsx - same phone-keyed roster convention (a
// MarketplaceStaff row is a regular SpotPool account, matched by phone, not a separate account
// type), minus the country/areas columns that don't apply here. Deactivate/Reactivate replaces
// Installers' hard delete since assignedStaffId on old orders must stay resolvable even after
// someone's taken off duty (see orders.ts's assignOrderStaff).
const MarketplaceStaff: FC = () => {
    const [state, setState] = useState<StateProps>({
        staff: [],
        loading: true,
        showModal: false,
        editingId: null,
        togglingId: null,
        saving: false,
        form: defaultForm,
        error: null,
        userOptions: [],
        usersLoading: false,
        selectedUser: null
    })
    const { staff, loading, showModal, editingId, togglingId, saving, form, error, userOptions, usersLoading, selectedUser } = state

    const setForm = (patch: Partial<FormState>) =>
        setState(s => ({ ...s, form: { ...s.form, ...patch } }))

    const getStaff = () => {
        setState(s => ({ ...s, loading: true }))
        Api({ controller: 'MarketplaceStaff', action: 'getMarketplaceStaff' })
            .then((resp: any) => setState(s => ({ ...s, staff: resp || [], loading: false })))
            .catch(() => setState(s => ({ ...s, loading: false })))
    }

    const searchUsers = (value: string) => {
        if (!value) return setState(s => ({ ...s, userOptions: [], usersLoading: false }))
        setState(s => ({ ...s, usersLoading: true }))
        Api({ controller: 'Users', action: 'searchUser', value })
            .then((resp: any) => setState(s => ({ ...s, userOptions: resp || [], usersLoading: false })))
            .catch(() => setState(s => ({ ...s, usersLoading: false })))
    }
    const debouncedSearchUsers = useCallback(_.debounce(searchUsers, 500), [])

    const openAdd = () => setState(s => ({
        ...s, showModal: true, editingId: null, form: defaultForm, userOptions: [], selectedUser: null
    }))

    const openEdit = (member: Staff) => {
        setState(s => ({
            ...s,
            showModal: true,
            editingId: member._id,
            selectedUser: null,
            form: { name: member.name }
        }))
    }

    const toggleActive = (member: Staff) => {
        setState(s => ({ ...s, togglingId: member._id }))
        Api({ controller: 'MarketplaceStaff', action: member.active ? 'deactivateMarketplaceStaff' : 'reactivateMarketplaceStaff', staffId: member._id })
            .then(() => {
                setState(s => ({ ...s, togglingId: null }))
                getStaff()
            })
            .catch((err: any) => setState(s => ({ ...s, togglingId: null, error: err?.message || 'Failed to update staff member.' })))
    }

    const saveStaff = () => {
        setState(s => ({ ...s, saving: true }))
        const payload: any = editingId
            ? { staffId: editingId, name: form.name }
            : { name: form.name, phone: selectedUser?.Phone }
        Api({ controller: 'MarketplaceStaff', action: editingId ? 'updateMarketplaceStaff' : 'addMarketplaceStaff', ...payload })
            .then((resp: any) => {
                if (resp?.error)
                    setState(s => ({ ...s, saving: false, error: resp.error.message }))
                else {
                    setState(s => ({ ...s, saving: false, showModal: false, editingId: null, form: defaultForm, selectedUser: null, userOptions: [] }))
                    getStaff()
                }
            })
            .catch((err: any) => setState(s => ({ ...s, saving: false, error: err?.message || 'Something went wrong.' })))
    }

    const canSave = !saving && !!form.name.trim() && (editingId ? true : !!selectedUser?.Phone)

    useEffect(() => { getStaff() }, [])

    return (
        <Box>
            <Box sx={{ padding: 2, textAlign: 'left' }}>
                <Typography sx={{ color: '#000', fontSize: 25, fontWeight: 'bold' }}>Marketplace Staff</Typography>
                <Typography sx={{ color: '#6b7280', fontSize: 14 }}>
                    Staff assigned to handle buyer communication for Marketplace orders. Sellers never contact
                    buyers directly - once an order is assigned here, that staff member sees it in their app.
                </Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: '5px', mb: 1 }}>
                <Button variant='contained' startIcon={<Add />} onClick={openAdd} sx={{ borderRadius: 2 }}>
                    Add Staff Member
                </Button>
            </Box>

            <Show>
                <Show.When isTrue={loading}>
                    <EmptyView loading={loading} oopTxt='No Staff' text='No marketplace staff found.' />
                </Show.When>
                <Show.Else>
                    <TableContainer component={Paper} sx={{ mt: 1, mx: '5px' }}>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: 'primary.main' }}>
                                    {headers.map(h => (
                                        <TableCell key={h} sx={{ color: 'white', fontWeight: 'bold' }}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {staff.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={headers.length} align='center' sx={{ color: '#9ca3af', py: 4 }}>
                                            No staff members yet. Add your first one.
                                        </TableCell>
                                    </TableRow>
                                ) : staff.map(member => (
                                    <TableRow key={member._id} hover>
                                        <TableCell sx={{ fontWeight: 600 }}>{member.name}</TableCell>
                                        <TableCell>{member.phone}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={member.active ? 'Active' : 'Inactive'}
                                                size='small'
                                                sx={{
                                                    bgcolor: member.active ? '#dcfce7' : '#f3f4f6',
                                                    color: member.active ? '#166534' : '#6b7280',
                                                    fontWeight: 600
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title='Edit'>
                                                <IconButton size='small' onClick={() => openEdit(member)} sx={{ color: 'primary.main' }}>
                                                    <Edit fontSize='small' />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title={member.active ? 'Deactivate' : 'Reactivate'}>
                                                <IconButton
                                                    size='small'
                                                    onClick={() => toggleActive(member)}
                                                    disabled={togglingId === member._id}
                                                    sx={{ color: member.active ? '#ef4444' : '#166534', ml: 0.5 }}
                                                >
                                                    {togglingId === member._id
                                                        ? <CircularProgress size={14} color='inherit' />
                                                        : member.active ? <Block fontSize='small' /> : <CheckCircleOutline fontSize='small' />}
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Show.Else>
            </Show>

            <Dialog open={showModal} onClose={() => !saving && setState(s => ({ ...s, showModal: false }))} fullWidth maxWidth='sm'>
                <DialogTitle sx={{ fontWeight: 'bold' }}>{editingId ? 'Edit Staff Member' : 'Add Staff Member'}</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
                    {!editingId && (
                        <Autocomplete
                            options={userOptions}
                            getOptionLabel={(o) => o.Username}
                            loading={usersLoading}
                            value={selectedUser}
                            onChange={(_, val) => setState(s => ({
                                ...s,
                                selectedUser: val,
                                form: { name: val?.Username || '' }
                            }))}
                            onInputChange={(_, val) => debouncedSearchUsers(val)}
                            isOptionEqualToValue={(o, v) => o._id === v._id}
                            noOptionsText='Type a username or phone number'
                            renderOption={(props, o) => (
                                <li {...props} key={o._id}>
                                    <Box>
                                        <Typography sx={{ fontWeight: 'bold', fontSize: 14 }}>{o.Username}</Typography>
                                        <Typography sx={{ color: '#727A8A', fontSize: 12 }}>{o.Phone}</Typography>
                                    </Box>
                                </li>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label='Search User'
                                    placeholder='Type a username or phone number...'
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {usersLoading ? <CircularProgress size={16} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        )
                                    }}
                                />
                            )}
                        />
                    )}
                    {selectedUser && (
                        <Typography sx={{ fontSize: 13, color: '#6b7280', mt: -1 }}>
                            Selected: {selectedUser.Username} · {selectedUser.Phone}
                        </Typography>
                    )}
                    <TextField
                        label='Display Name'
                        value={form.name}
                        onChange={e => setForm({ name: e.target.value })}
                        fullWidth
                        size='small'
                        disabled={!editingId && !selectedUser}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setState(s => ({ ...s, showModal: false }))} disabled={saving}>Cancel</Button>
                    <Button
                        variant='contained'
                        onClick={saveStaff}
                        disabled={!canSave}
                        startIcon={saving ? <CircularProgress size={16} color='inherit' /> : undefined}
                    >
                        {editingId ? 'Update' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={Boolean(error)}
                autoHideDuration={4000}
                onClose={() => setState(s => ({ ...s, error: null }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity='error' onClose={() => setState(s => ({ ...s, error: null }))} sx={{ width: '100%' }}>
                    {error}
                </Alert>
            </Snackbar>
        </Box>
    )
}

export default MarketplaceStaff
